import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Eye, Download, FileText, Receipt, Loader2, Printer, Image as ImageIcon, X, CheckCircle2, Clock, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { formatINR, fmtDateTime } from './payoutUtils';

const stripApi = (u) => u.replace(/^\/api/, '');

export const openSecure = async (api, url, download = false, filename = 'proof') => {
  try {
    const res = await api.get(stripApi(url), { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    if (download) { const a = document.createElement('a'); a.href = blobUrl; a.download = filename; a.click(); }
    else window.open(blobUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch { toast.error('Unable to open file'); }
};

// Authenticated <img>: fetches the proof as a blob (no public URLs)
const AuthImage = ({ url, alt, className, onClick }) => {
  const { api } = useAuth();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    api.get(stripApi(url), { responseType: 'blob' }).then(r => { revoke = URL.createObjectURL(r.data); setSrc(revoke); }).catch(() => {});
    return () => revoke && URL.revokeObjectURL(revoke);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  if (!src) return <div className={`${className} bg-muted animate-pulse`} />;
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
};

// Full-screen lightbox for screenshot proofs (portal so it sits above dialogs/sheets)
const Lightbox = ({ proof, onClose, api }) => {
  if (!proof) return null;
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-6" onClick={onClose} data-testid="proof-lightbox">
      <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={onClose} data-testid="proof-lightbox-close"><X className="h-6 w-6" /></button>
      <div className="max-w-5xl max-h-full flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
        {proof.is_image ? <AuthImage url={proof.view_url} alt={proof.original_filename} className="max-h-[80vh] max-w-full object-contain rounded-sm shadow-2xl" />
          : <div className="text-white text-center"><FileText className="h-16 w-16 mx-auto mb-2" />PDF proof — use Open to view</div>}
        <div className="flex items-center gap-2 text-white/90 text-xs">
          <span className="truncate max-w-[50vw]">{proof.original_filename}</span>
          <Button size="sm" variant="secondary" className="h-7 rounded-sm" onClick={() => openSecure(api, proof.view_url)}><Eye className="h-3.5 w-3.5 mr-1" />Open</Button>
          <Button size="sm" variant="secondary" className="h-7 rounded-sm" onClick={() => openSecure(api, proof.download_url, true, proof.original_filename)} data-testid="proof-lightbox-download"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
        </div>
      </div>
    </div>, document.body);
};

// Stored receipt rendered from backend data (never regenerated differently)
const ReceiptBody = ({ r, inline = false }) => {
  const details = [
    ['UTR Number', r.utr || (r.method === 'screenshot' ? 'Screenshot settlement' : '—'), 'utr-number'],
    ['Order No', r.order_id, 'order-no'],
    ...(r.external_ref ? [['Gateway Ref', r.external_ref, 'gateway-ref']] : []),
    ['Bank Account', r.bank_account, 'bank-account'], ['IFSC', r.ifsc, 'ifsc'],
    ['Beneficiary Name', r.account_holder, 'beneficiary-name'], ['Bank Name', r.bank_name, 'bank-name'],
  ];
  return (
    <div className={inline ? 'rounded-sm border border-border/60 bg-card' : ''} data-testid="receipt-body">
      <div className={`${inline ? 'px-4 pt-4 pb-3' : 'px-6 pt-6 pb-4'} border-b border-border/60`}>
        <div className="flex items-center justify-between mb-2">
          <p className="font-heading text-base font-bold">Awadh<span className="text-primary">Pay</span></p>
          <p className="text-[11px] text-muted-foreground">Payout Receipt · <span className="font-mono" data-testid="receipt-no">{r.receipt_no}</span></p>
        </div>
        <p className={`${inline ? 'text-2xl' : 'text-3xl'} font-heading font-bold font-mono`} data-testid="receipt-amount">{formatINR(r.amount, 2)}</p>
        <div className="flex items-center justify-between mt-2 text-sm">
          <span className="inline-flex items-center gap-2 text-green-600 font-medium" data-testid="receipt-status"><span className="w-2 h-2 rounded-full bg-green-500" />Successful</span>
          <span className="text-muted-foreground text-xs" data-testid="receipt-transaction-date-time">{fmtDateTime(r.transaction_time)}</span>
        </div>
      </div>
      <div className={inline ? 'px-4 py-3' : 'px-6 py-4'}>
        <p className="text-sm font-semibold mb-1">Payout <span className="text-muted-foreground font-normal">details</span></p>
        <div className="text-xs">
          {details.map(([k, v, tid]) => (
            <div key={k} className="grid grid-cols-[120px_1fr] gap-3 py-1.5 border-b border-border/30 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-medium font-mono break-all" data-testid={`receipt-${tid}`}>{v || '—'}</span></div>
          ))}
        </div>
        {r.requested_amount && Number(r.requested_amount) !== Number(r.amount) && <p className="text-[11px] text-muted-foreground mt-2">Part settlement of {formatINR(r.requested_amount)} payout ({r.order_id}).</p>}
      </div>
    </div>
  );
};

export const downloadReceiptPdf = (api, settlementId, receiptNo) =>
  openSecure(api, `/api/payout-settlements/receipts/${settlementId}/pdf`, true, `${receiptNo || settlementId}.pdf`);

export const ReceiptDialog = ({ settlementId, open, onOpenChange }) => {
  const { api } = useAuth();
  const [r, setR] = useState(null);
  useEffect(() => {
    if (!open || !settlementId) return;
    setR(null);
    api.get(`/payout-settlements/receipts/${settlementId}`).then(res => setR(res.data)).catch(() => toast.error('Unable to load receipt'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settlementId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm p-0 overflow-hidden" data-testid="receipt-dialog">
        <DialogHeader className="sr-only"><DialogTitle>Payout Receipt</DialogTitle><DialogDescription>AwadhPay payout receipt</DialogDescription></DialogHeader>
        {!r ? <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…</div> : (
          <div>
            <ReceiptBody r={r} />
            <div className="flex items-center justify-between px-6 pb-5">
              <p className="text-[10px] text-muted-foreground">System generated · {fmtDateTime(r.generated_at)}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="receipt-print-btn"><Printer className="h-4 w-4 mr-1" />Print</Button>
                <Button size="sm" className="rounded-sm" onClick={() => downloadReceiptPdf(api, r.settlement_id, r.receipt_no)} data-testid="receipt-download-btn"><Download className="h-4 w-4 mr-1" />Download PDF</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const STATUS_LABEL = { completed: ['Settlement Verified', CheckCircle2, 'text-green-600'], proof_submitted: ['Proof submitted — pending validation', Clock, 'text-amber-600'], validated: ['Validated — completing', Clock, 'text-amber-600'], proof_invalid: ['Proof invalid — action required', AlertTriangle, 'text-red-600'], none: ['Settlement proof pending', Clock, 'text-muted-foreground'] };
const METHOD_LABEL = { utr: 'UTR', screenshot: 'Screenshot', excel: 'Excel' };

// Settlement / Payment Proof block — shared by admin modal + merchant dialog. Always reads live backend data.
export const SettlementDetails = ({ payoutId, refreshKey = 0, compact = false, showInlineReceipt = true, hideWhenEmpty = false, onLoaded }) => {
  const { api } = useAuth();
  const [s, setS] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [inlineReceipt, setInlineReceipt] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    if (!payoutId) return;
    api.get(`/payout-settlements/${payoutId}`).then(r => { setS(r.data); onLoaded?.(r.data); }).catch(() => setS(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutId, refreshKey]);
  const latest = s?.settlements?.length ? s.settlements[s.settlements.length - 1] : null;
  useEffect(() => {
    if (!showInlineReceipt || !latest) { setInlineReceipt(null); return; }
    api.get(`/payout-settlements/receipts/${latest.id}`).then(r => setInlineReceipt(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?.id, showInlineReceipt]);

  if (!s) return null;
  const empty = s.proofs.length === 0 && s.settlements.length === 0 && !s.legacy_utr;
  if (s.status !== 'paid' && empty && (hideWhenEmpty || !['approved', 'processing'].includes(s.status))) return null;
  if (s.status === 'paid' && empty && hideWhenEmpty) return null;
  const [label, Icon, cls] = STATUS_LABEL[s.settlement_status] || STATUS_LABEL.none;
  const images = s.proofs.filter(p => p.is_image);
  const files = s.proofs.filter(p => !p.is_image);
  const total = s.settlements.reduce((a, x) => a + (x.amount || 0), 0);

  return (
    <section className="space-y-3" data-testid="settlement-details">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Settlement / Payment Proof</p>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cls}`} data-testid="settlement-status"><Icon className="h-3.5 w-3.5" />{label}{s.settlement_method ? ` · ${METHOD_LABEL[s.settlement_method] || s.settlement_method}` : ''}</span>
      </div>
      {s.settlement_errors?.length > 0 && <div className="p-2.5 rounded-sm border border-red-500/40 bg-red-500/5 text-xs text-red-600">{s.settlement_errors.join(' · ')}</div>}

      {s.settlement_method === 'excel' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="excel-settlement-summary">
          {[['Settlement Method', 'Excel', FileSpreadsheet], ['Matched Records', s.settlements.length], ['Total Settled', formatINR(s.settled_amount, 2)], ['Validation', 'Verified']].map(([k, v]) => (
            <div key={k} className="p-2 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">{k}</p><p className={`font-medium ${k === 'Validation' ? 'text-green-600' : ''}`}>{v}</p></div>
          ))}
        </div>
      )}

      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-3'} gap-2 text-sm`}>
        <div className="p-2.5 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">Requested</p><p className="font-medium font-mono">{formatINR(s.requested_amount, 2)}</p></div>
        <div className="p-2.5 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">Settled</p><p className="font-medium font-mono text-green-600" data-testid="settled-amount">{formatINR(s.settled_amount, 2)}</p></div>
        {!compact && <div className="p-2.5 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">Records</p><p className="font-medium">{s.settlements.length} settlement{s.settlements.length === 1 ? '' : 's'} · {s.proofs.length} proof{s.proofs.length === 1 ? '' : 's'}</p></div>}
      </div>

      {(s.settlements.length > 0 || s.legacy_utr) && (
        <div className="rounded-sm border border-border/50 overflow-hidden" data-testid="settlement-utr-table">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/40"><span>UTR</span><span className="text-right">Amount</span><span className="w-24 text-right">Receipt</span></div>
          {s.settlements.map(x => (
            <div key={x.id} className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-sm border-t border-border/40 items-center" data-testid={`settlement-row-${x.id}`}>
              <span className="font-mono text-xs truncate">{x.utr || <span className="text-muted-foreground inline-flex items-center gap-1"><ImageIcon className="h-3 w-3" />Screenshot</span>}<span className="block text-[10px] text-muted-foreground">{fmtDateTime(x.transaction_time)}{x.external_ref ? ` · ${x.external_ref}` : ''}</span></span>
              <span className="font-mono text-right">{formatINR(x.amount, 2)}</span>
              <Button variant="ghost" size="sm" className="h-7 px-2 rounded-sm text-xs w-24 justify-end" onClick={() => setReceipt(x.id)} data-testid={`receipt-btn-${x.id}`}><Receipt className="h-3.5 w-3.5 mr-1" />{x.receipt_no?.replace('RCPT-', '#')}</Button>
            </div>
          ))}
          {s.settlements.length > 1 && <div className="flex justify-between px-3 py-2 text-sm border-t border-border/60 bg-muted/30 font-medium"><span>Total Settled</span><span className="font-mono" data-testid="settlement-total">{formatINR(total, 2)}</span></div>}
          {s.legacy_utr && <div className="px-3 py-2 text-sm border-t border-border/40 font-mono text-xs">{s.legacy_utr} <span className="text-muted-foreground">· {formatINR(s.requested_amount, 2)}</span></div>}
        </div>
      )}

      {showInlineReceipt && inlineReceipt && (
        <div className="space-y-2" data-testid="inline-receipt">
          <p className="text-xs text-muted-foreground">Payment Receipt{s.settlements.length > 1 ? ` (latest of ${s.settlements.length})` : ''}</p>
          <ReceiptBody r={inlineReceipt} inline />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReceipt(latest.id)} data-testid="view-receipt-btn"><Eye className="h-3.5 w-3.5 mr-1" />View Receipt</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => downloadReceiptPdf(api, latest.id, latest.receipt_no)} data-testid="download-receipt-btn"><Download className="h-3.5 w-3.5 mr-1" />Download Receipt</Button>
          </div>
        </div>
      )}

      {s.proofs.length > 0 && (
        <div className="space-y-2" data-testid="settlement-proofs">
          <p className="text-xs text-muted-foreground">Payment Proof — {s.proofs.length} file{s.proofs.length === 1 ? '' : 's'}</p>
          {images.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((p, i) => (
                <div key={p.id} className="group relative rounded-sm border border-border/50 overflow-hidden bg-muted/30" data-testid={`proof-${p.id}`}>
                  <AuthImage url={p.view_url} alt={p.original_filename} className="h-24 w-full object-cover cursor-zoom-in" onClick={() => setLightbox(p)} />
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-1.5 py-1 bg-black/55 text-white text-[10px]">
                    <span className="truncate">Preview {i + 1}</span>
                    <button onClick={() => openSecure(api, p.download_url, true, p.original_filename)} className="hover:text-primary" data-testid={`proof-download-${p.id}`}><Download className="h-3 w-3" /></button>
                  </div>
                  <button className="absolute inset-0" aria-label="Preview" onClick={() => setLightbox(p)} data-testid={`proof-view-${p.id}`} />
                </div>
              ))}
            </div>
          )}
          {files.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-sm border border-border/50 text-sm" data-testid={`proof-${p.id}`}>
              <div className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-muted-foreground shrink-0" /><div className="min-w-0"><p className="truncate text-xs font-medium">{p.original_filename}</p><p className="text-[10px] text-muted-foreground">{fmtDateTime(p.uploaded_at)} · {Math.max(1, Math.round((p.size || 0) / 1024))} KB</p></div></div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={() => openSecure(api, p.view_url)} data-testid={`proof-view-${p.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={() => openSecure(api, p.download_url, true, p.original_filename)} data-testid={`proof-download-${p.id}`}><Download className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ReceiptDialog settlementId={receipt} open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)} />
      <Lightbox proof={lightbox} onClose={() => setLightbox(null)} api={api} />
    </section>
  );
};
